import React, { createContext, useContext, useReducer, useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type {
  GenUIState,
  GenUIStateAction,
  GenUIContextType,
  GenUIMode,
  GenUIMessage,
  GenUIAction,
} from '@/types/genui';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';

// ─── localStorage persistence ────────────────────────────────────────────────

const CHAT_STORAGE_KEY = 'genui_chat_messages';

function loadStoredMessages(): GenUIMessage[] {
  try {
    const raw = localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Array<Record<string, unknown>>;
    return parsed.map((m) => ({
      ...(m as unknown as GenUIMessage),
      timestamp: new Date(m.timestamp as string),
      // Reset any in-flight streaming state so the UI renders cleanly on load
      status: (m.status === 'streaming' ? 'complete' : m.status) as GenUIMessage['status'],
    }));
  } catch {
    return [];
  }
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

const initialState: GenUIState = {
  mode: 'traditional',
  activeConversationId: null,
  conversations: [],
  messages: loadStoredMessages(),
  isStreaming: false,
  isHistoryPanelOpen: false,
  inputValue: '',
  error: null,
  agentType: 'general',
};

function genUIReducer(state: GenUIState, action: GenUIStateAction): GenUIState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.mode };

    case 'SET_ACTIVE_CONVERSATION':
      return { ...state, activeConversationId: action.conversationId };

    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.conversations };

    case 'ADD_USER_MESSAGE':
      return { ...state, messages: [...state.messages, action.message] };

    case 'START_ASSISTANT_MESSAGE':
      return {
        ...state,
        isStreaming: true,
        messages: [
          ...state.messages,
          {
            id: action.messageId,
            role: 'assistant',
            content: '',
            toolCalls: [],
            timestamp: new Date(),
            status: 'streaming',
          },
        ],
      };

    case 'APPEND_DELTA': {
      const msgs = [...state.messages];
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && lastMsg.role === 'assistant' && lastMsg.status === 'streaming') {
        msgs[msgs.length - 1] = { ...lastMsg, content: lastMsg.content + action.content };
      }
      return { ...state, messages: msgs };
    }

    case 'ADD_TOOL_CALL_START': {
      const msgs = [...state.messages];
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && lastMsg.role === 'assistant') {
        const toolCalls = [...(lastMsg.toolCalls || [])];
        toolCalls.push({
          id: action.toolCallId,
          toolName: action.toolName,
          data: null,
          status: 'pending',
        });
        msgs[msgs.length - 1] = { ...lastMsg, toolCalls };
      }
      return { ...state, messages: msgs };
    }

    case 'ADD_TOOL_CALL_RESULT': {
      const msgs = [...state.messages];
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && lastMsg.role === 'assistant') {
        const toolCalls = (lastMsg.toolCalls || []).map((tc) =>
          tc.id === action.toolCallId
            ? {
                ...tc,
                data: action.data,
                status: (action.success ? 'completed' : 'error') as 'completed' | 'error',
                error: action.error,
                componentHint: action.componentHint,
                isIntermediate: action.isIntermediate,
              }
            : tc
        );
        msgs[msgs.length - 1] = { ...lastMsg, toolCalls };
      }
      return { ...state, messages: msgs };
    }

    case 'FINISH_ASSISTANT_MESSAGE': {
      const msgs = [...state.messages];
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && lastMsg.role === 'assistant') {
        msgs[msgs.length - 1] = { ...lastMsg, status: 'complete', suggestions: action.suggestions };
      }
      return { ...state, messages: msgs, isStreaming: false };
    }

    case 'SET_STREAMING':
      return { ...state, isStreaming: action.isStreaming };

    case 'SET_HISTORY_PANEL_OPEN':
      return { ...state, isHistoryPanelOpen: action.isOpen };

    case 'SET_INPUT_VALUE':
      return { ...state, inputValue: action.value };

    case 'SET_ERROR':
      return { ...state, error: action.error, isStreaming: false };

    case 'CLEAR_MESSAGES':
      return { ...state, messages: [], activeConversationId: null };

    case 'SET_MESSAGES':
      return { ...state, messages: action.messages };

    case 'SET_AGENT_TYPE':
      return { ...state, agentType: action.agentType };

    default:
      return state;
  }
}

// ─── Tool → Query Key Invalidation Map ──────────────────────────────────────

const TOOL_QUERY_INVALIDATION: Record<string, string[][]> = {
  // Contacts
  contacts_create: [['contacts']],
  contacts_update: [['contacts']],
  contacts_delete: [['contacts']],
  contacts_import: [['contacts']],
  contacts_add_tags: [['contacts']],
  contacts_remove_tags: [['contacts']],
  // Inbox
  inbox_send_message: [['conversations'], ['messages']],
  inbox_send_media: [['conversations'], ['messages']],
  inbox_assign_agent: [['conversations']],
  inbox_close_conversation: [['conversations']],
  inbox_reopen_conversation: [['conversations']],
  inbox_mark_read: [['conversations']],
  // Campaigns
  campaigns_create: [['campaigns']],
  campaigns_send: [['campaigns']],
  campaigns_delete: [['campaigns']],
  // Templates — MUST be 'meta-templates' (the key useMetaTemplates uses
  // at queryKey: ['meta-templates', businessAccountId, filters]). The
  // legacy key 'templates' was a regression flagged in genui.md: with
  // that key, GenUI-created templates land in the DB but the Templates
  // page React Query cache never refetches, so the user can't see them
  // without a manual page refresh.
  templates_create: [['meta-templates']],
  templates_delete: [['meta-templates']],
  templates_sync: [['meta-templates']],
  // Automation
  automation_create_flow: [['automation-flows']],
  automation_update_flow: [['automation-flows']],
  automation_delete_flow: [['automation-flows']],
  automation_toggle_flow: [['automation-flows']],
  // Commerce
  commerce_create_invoice: [['invoices']],
  commerce_update_order_status: [['orders']],
  // Payment links
  payment_link_create: [['payment-links']],
  payment_link_cancel: [['payment-links']],
  // CRM
  crm_create_lead: [['leads']],
  crm_update_lead: [['leads']],
  crm_delete_lead: [['leads']],
  crm_add_note: [['leads']],
  // Data Store
  datastore_create_store: [['data-stores']],
  datastore_create_record: [['data-stores']],
  datastore_update_record: [['data-stores']],
  datastore_delete_record: [['data-stores']],
  datastore_add_field: [['data-stores']],
  // Quick Replies
  quickreply_create: [['quick-replies']],
  quickreply_update: [['quick-replies']],
  quickreply_delete: [['quick-replies']],
  // Scheduled Messages
  scheduled_message_create: [['scheduled-messages']],
  scheduled_message_cancel: [['scheduled-messages']],
  // Social Media
  social_create_post: [['social-posts']],
  social_publish_post: [['social-posts']],
  social_delete_post: [['social-posts']],
  // Scheduling
  scheduling_create_event_type: [['scheduling']],
  scheduling_cancel_booking: [['scheduling']],
  // Settings
  settings_invite_member: [['team']],
  settings_remove_member: [['team']],
  settings_update_profile: [['profile']],
  // WhatsApp
  whatsapp_create_reminder: [['reminders']],
  // Notifications
  notifications_mark_read: [['notifications']],
  notifications_mark_all_read: [['notifications']],
  // Media
  media_delete: [['media']],
};

// ─── Tool Name → Component Hint Mapping ─────────────────────────────────────

const TOOL_TO_COMPONENT_HINT: Record<string, string> = {
  get_dashboard_stats:   'DashboardStatsCard',
  search_contacts:       'ContactListCard',
  list_campaigns:        'CampaignListCard',
  get_conversations:     'ConversationListCard',
  list_orders:           'OrderListCard',
  list_products:         'ProductListCard',
  list_automation_flows: 'FlowListCard',
  get_analytics:         'AnalyticsSummaryCard',
  agent_redirect:        'AgentRedirectCard',
  get_social_posts:      'SocialPostsCard',
  get_social_accounts:      'SocialAccountsCard',
  get_integration_status:   'IntegrationStatusCard',
  navigate_to_page:         'NavigationCard',
  show_campaign_form:    'CampaignFormCard',
  get_commerce_stats:    'CommerceStatsCard',
  get_templates:         'TemplateListCard',
  search_templates:      'TemplateListCard',
  get_team_members:      'TeamMembersCard',
  get_subscription_info: 'BillingSummaryCard',
  list_bookings:         'ScheduleOverviewCard',
  list_event_types:      'EventTypeListCard',
  create_event_type:     'SuccessCard',
  settings_show_connect_form: 'ConnectIntegrationCard',

  // New GenUI Tool names mapping
  inbox_list_conversations: 'ConversationListCard',
  inbox_get_conversation:  'ConversationDetailCard',
  inbox_send_message:      'SuccessCard',
  inbox_assign_agent:      'AgentAssignmentCard',
  inbox_assign_to_member:  'AgentAssignmentCard',
  inbox_mark_read:         'StatusConfirmation',
  inbox_get_unread_count:  'UnreadCountCard',
  inbox_search_conversations: 'ConversationListCard',
  inbox_get_summary:       'InboxSummaryCard',
  inbox_mark_all_read:     'SuccessCard',
  inbox_get_messages:      'MessageListCard',
  inbox_create_conversation: 'ConversationDetailCard',
  inbox_step_in:           'SuccessCard',
  inbox_star_message:      'SuccessCard',
  inbox_unstar_message:    'SuccessCard',
  inbox_get_starred_messages: 'MessageListCard',
  inbox_forward_message:   'SuccessCard',
  inbox_get_starred_conversations: 'ConversationListCard',
  inbox_add_reaction:      'SuccessCard',
  inbox_remove_reaction:   'SuccessCard',
  inbox_close_conversation: 'SuccessCard',
  inbox_reopen_conversation: 'SuccessCard',

  contacts_list:           'ContactListCard',
  contacts_get:            'ContactDetailCard',
  contacts_create:         'ContactDetailCard',
  contacts_update:         'ContactDetailCard',
  contacts_search:         'ContactListCard',
  contacts_add_tags:       'SuccessCard',
  contacts_show_tag_form:  'TagFormCard',
  contacts_assign_tag:     'TagAssignedCard',
  contacts_delete:         'SuccessCard',
  contacts_list_tags:      'TagListCard',
  contacts_delete_tag:     'SuccessCard',
  contacts_block:          'SuccessCard',
  contacts_unblock:        'SuccessCard',
  contacts_bulk_add_tags:  'SuccessCard',
  contacts_pause_flows:    'SuccessCard',
  contacts_remove_tags:    'SuccessCard',
  contacts_update_tag:     'SuccessCard',
  contacts_get_stats:      'StatsCard',
  contacts_get_growth:     'GrowthChartCard',
  contacts_bulk_import:    'ImportStatusCard',

  // Template creation — renders inline form card, no navigation
  templates_create:        'TemplateFormCard',
  campaigns_create:        'CampaignDetailCard',
};

// ─── Persisted-turn → frontend toolCalls rehydration ─────────────────────────
//
// A saved assistant turn stores three PARALLEL fields (see backend
// ConversationManager.saveTurn): `tool_calls` (OpenAI shape:
// { id, type, function: { name, arguments } }), `tool_results`
// ([{ tool_call_id, content }] where content is the real card data), and
// `component_hints` (array of hint strings). The live streaming path builds the
// rich frontend shape ({ toolName, data, componentHint, status }) itself, but
// when a past chat is reloaded from history we must rebuild that shape here —
// otherwise the raw OpenAI tool_calls (no toolName/data/componentHint) collapse
// to the generic "Tool" fallback card. This makes reloaded chats render the
// same stable rich cards as the live session.
function rehydrateToolCalls(msg: Record<string, unknown>): NonNullable<GenUIMessage['toolCalls']> {
  const rawCalls = Array.isArray(msg.tool_calls) ? (msg.tool_calls as any[]) : [];
  const rawResults = Array.isArray(msg.tool_results) ? (msg.tool_results as any[]) : [];
  const rawHints = Array.isArray(msg.component_hints) ? (msg.component_hints as any[]) : [];
  // The persisted `component_hints` array only stores an entry when a hint
  // existed, so it can be shorter than `tool_calls`. Only trust it positionally
  // when the two arrays line up 1:1 — otherwise it may map the wrong card, and
  // we fall back to the deterministic toolName → hint map below.
  const hintsAligned = rawHints.length === rawCalls.length;

  return rawCalls.map((tc: any, i: number) => {
    // Support both shapes: the OpenAI persisted shape and (defensively) an
    // already-frontend-shaped entry, so this is safe regardless of source.
    const id: string = tc?.id ?? `tc-${(msg.id as string) ?? 'msg'}-${i}`;
    const toolName: string = tc?.toolName ?? tc?.function?.name ?? '';

    const matched = rawResults.find((r: any) => r?.tool_call_id === tc?.id);
    const data = tc?.data ?? (matched ? matched.content : undefined);

    // A failed tool result is persisted as content `{ error: <msg> }` with no
    // `success: true`. Treat that as an error card; everything else is completed.
    const isError =
      !!data &&
      typeof data === 'object' &&
      typeof (data as any).error === 'string' &&
      (data as any).success !== true;

    const componentHint: string | undefined =
      tc?.componentHint ?? (hintsAligned ? rawHints[i] : undefined) ?? TOOL_TO_COMPONENT_HINT[toolName];

    return {
      id,
      toolName,
      data,
      status: (isError ? 'error' : 'completed') as 'completed' | 'error',
      error: isError ? String((data as any).error) : undefined,
      componentHint,
    };
  });
}

// ─── Context ─────────────────────────────────────────────────────────────────

const GenUIContext = createContext<GenUIContextType | undefined>(undefined);

export const GenUIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(genUIReducer, initialState);
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isSendingRef = useRef(false);

  // Get business account context
  let businessAccountId: string | null = null;
  try {
    const baContext = useBusinessAccountContext();
    businessAccountId = baContext?.selectedAccountId || null;
  } catch {
    // BusinessAccountContext may not be available in all contexts
  }

  const sendMessage = useCallback(async (message: string, files?: File[]) => {
    if (!message.trim() || state.isStreaming || isSendingRef.current) return;
    isSendingRef.current = true;
    // Clear any previous error immediately so the banner auto-dismisses when the
    // user retries (Regenerate, new message, or suggestion click).
    dispatch({ type: 'SET_ERROR', error: null });
    if (!businessAccountId) {
      dispatch({ type: 'SET_ERROR', error: 'Please select a business account first.' });
      isSendingRef.current = false;
      return;
    }

    // Upload files first so we can attach metadata to the message
    let attachmentText = '';
    let uploadedAttachments: Array<{ fileId: string; fileName: string; fileType: string; fileSize: number; s3Key: string; s3Url: string }> = [];
    if (files && files.length > 0) {
      try {
        const { uploadFile } = await import('@/lib/api/services/genui');
        const uploadResults = await Promise.all(files.map((file) => uploadFile(file, businessAccountId)));
        const successful = uploadResults.filter((r) => r.success && r.data).map((r) => r.data!);
        uploadedAttachments = successful;
        if (successful.length > 0) {
          attachmentText = `\n[Attachments: ${successful.map((a) => a.fileName).join(', ')}]`;
        }
        const failed = uploadResults.filter((r) => !r.success);
        if (failed.length > 0) {
          dispatch({ type: 'SET_ERROR', error: `Failed to upload ${failed.length} file(s)` });
        }
      } catch {
        dispatch({ type: 'SET_ERROR', error: 'Failed to upload files' });
      }
    }

    // Add user message to UI
    const userMsg: GenUIMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message.trim() + attachmentText,
      timestamp: new Date(),
      status: 'complete',
    };
    dispatch({ type: 'ADD_USER_MESSAGE', message: userMsg });
    dispatch({ type: 'SET_INPUT_VALUE', value: '' });

    // Start assistant streaming placeholder
    const assistantId = `assistant-${Date.now()}`;
    dispatch({ type: 'START_ASSISTANT_MESSAGE', messageId: assistantId });

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let receivedDone = false;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/genui/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: message.trim(),
          conversationId: state.activeConversationId || undefined,
          businessAccountId,
          agentType: state.agentType !== 'general' ? state.agentType : undefined,
          attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Parse SSE stream
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const json = line.slice(6).trim();
          if (!json) continue;

          let event: { type: string; data: Record<string, unknown> };
          try { event = JSON.parse(json); } catch { continue; }

          switch (event.type) {
            case 'conversation_created':
              dispatch({ type: 'SET_ACTIVE_CONVERSATION', conversationId: event.data.conversationId as string });
              break;

            case 'delta':
              dispatch({ type: 'APPEND_DELTA', content: event.data.content as string });
              break;

            case 'tool_call_start':
              dispatch({
                type: 'ADD_TOOL_CALL_START',
                toolCallId: event.data.toolCallId as string,
                toolName: event.data.toolName as string,
              });
              break;

            case 'tool_call_result': {
              const toolName = event.data.toolName as string;
              const isIntermediate = event.data.isIntermediate as boolean | undefined;
              const success = event.data.success as boolean;
              const resultObj = event.data.result as any;

              dispatch({
                type: 'ADD_TOOL_CALL_RESULT',
                toolCallId: event.data.toolCallId as string,
                toolName,
                data: event.data.result,
                success,
                error: event.data.error as string | undefined,
                componentHint: isIntermediate ? undefined : ((event.data.componentHint as string | undefined) ?? TOOL_TO_COMPONENT_HINT[toolName]),
                isIntermediate,
              });

              // Intercept templates_create tool call — just let TemplateFormCard render inline.
              // No sessionStorage, no navigation, no event dispatch needed.

              const keys = TOOL_QUERY_INVALIDATION[toolName];
              if (keys) keys.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
              break;
            }

            case 'usage':
              try {
                console.log('[GenUI] token usage', event.data);
                window.dispatchEvent(new CustomEvent('genui:usage', { detail: event.data }));
              } catch {
                // Non-fatal: usage visibility should never break the stream.
              }
              break;

            case 'done':
              receivedDone = true;
              dispatch({ type: 'FINISH_ASSISTANT_MESSAGE' });
              break;

            case 'error':
              dispatch({ type: 'SET_ERROR', error: (event.data.message as string) || 'An error occurred' });
              break;
          }
        }
      }

      // Safety net: if stream ended without a done event
      if (!receivedDone) {
        dispatch({ type: 'FINISH_ASSISTANT_MESSAGE' });
      }
    } catch (error: unknown) {
      if ((error as Error).name === 'AbortError') {
        dispatch({ type: 'FINISH_ASSISTANT_MESSAGE' });
        isSendingRef.current = false;
        return;
      }
      dispatch({ type: 'SET_ERROR', error: (error as Error).message || 'Failed to send message' });
      dispatch({ type: 'FINISH_ASSISTANT_MESSAGE' });
    } finally {
      isSendingRef.current = false;
    }
  }, [state.isStreaming, state.activeConversationId, state.agentType, businessAccountId, queryClient]);

  const cancelStream = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const switchMode = useCallback((mode: GenUIMode) => {
    dispatch({ type: 'SET_MODE', mode });
  }, []);

  // Persist completed messages to localStorage so they survive page refresh
  useEffect(() => {
    const completed = state.messages.filter((m) => m.status !== 'streaming');
    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(completed));
    } catch {
      // Ignore quota errors
    }
  }, [state.messages]);

  const newConversation = useCallback(() => {
    localStorage.removeItem(CHAT_STORAGE_KEY);
    dispatch({ type: 'CLEAR_MESSAGES' });
  }, []);

  const loadConversation = useCallback(async (conversationId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/v1/genui/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();

      if (result.success && result.data) {
        const messages: GenUIMessage[] = result.data.map((msg: Record<string, unknown>) => ({
          id: msg.id as string,
          role: msg.role as GenUIMessage['role'],
          content: msg.content as string,
          toolCalls: rehydrateToolCalls(msg),
          timestamp: new Date(msg.created_at as string),
          status: 'complete' as const,
        }));

        // Older turns were persisted with the user + assistant messages sharing
        // the same millisecond, so the backend's created_at ordering could tie
        // and surface the answer above the question. Stable-sort by timestamp,
        // and for ties within a turn render user → assistant → tool. New turns
        // (saved with distinct timestamps) are already correct and unaffected.
        const ROLE_ORDER: Record<string, number> = { user: 0, assistant: 1, tool: 2 };
        messages.sort((a, b) => {
          const ta = a.timestamp.getTime();
          const tb = b.timestamp.getTime();
          if (ta !== tb) return ta - tb;
          return (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9);
        });

        dispatch({ type: 'SET_MESSAGES', messages });
        dispatch({ type: 'SET_ACTIVE_CONVERSATION', conversationId });
      }
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        error: 'Failed to load conversation',
      });
    }
  }, []);

  const handleAction = useCallback((action: GenUIAction) => {
    switch (action.type) {
      case 'navigate':
        switchMode('traditional');
        if (action.payload.url || action.payload.path) {
          navigate((action.payload.url || action.payload.path) as string);
        }
        break;

      case 'continue_chat':
        if (action.payload.message) {
          sendMessage(action.payload.message as string);
        }
        break;

      case 'switch_agent':
        if (action.payload.agentId && typeof action.payload.agentId === 'string') {
          dispatch({ type: 'SET_AGENT_TYPE', agentType: action.payload.agentId });
          dispatch({ type: 'CLEAR_MESSAGES' });
          localStorage.removeItem('genui_chat_messages');
        }
        break;

      default:
        break;
    }
  }, [navigate, switchMode, sendMessage, dispatch]);

  // Chat widget open state — independent of GenUI mode
  const isOpen = isChatWidgetOpen;
  const setIsOpen = useCallback((open: boolean) => {
    setIsChatWidgetOpen(open);
  }, []);
  const toggleChat = useCallback(() => {
    setIsChatWidgetOpen((prev) => !prev);
  }, []);

  const contextValue: GenUIContextType = {
    state,
    dispatch,
    sendMessage,
    cancelStream,
    switchMode,
    newConversation,
    loadConversation,
    handleAction,
    isOpen,
    setIsOpen,
    toggleChat,
  };

  return (
    <GenUIContext.Provider value={contextValue}>
      {children}
    </GenUIContext.Provider>
  );
};

export const useGenUI = (): GenUIContextType => {
  const context = useContext(GenUIContext);
  if (context === undefined) {
    throw new Error('useGenUI must be used within a GenUIProvider');
  }
  return context;
};

// Backward compatibility hook
export const useAiChat = () => {
  const { isOpen, setIsOpen, toggleChat } = useGenUI();
  return { isOpen, setIsOpen, toggleChat };
};
